import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, XCircle, Clock, FlaskConical, User, Calendar, Stethoscope, MapPin, FileText } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyResults() {
  const { folio, token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVerification = async () => {
      try {
        const endpoint = token
          ? `${API_URL}/api/verify/t/${token}`
          : `${API_URL}/api/verify/${folio}`;
        const res = await fetch(endpoint);
        if (!res.ok) {
          if (res.status === 404) throw new Error("not_found");
          throw new Error("server_error");
        }
        const json = await res.json();
        setData(json);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchVerification();
  }, [folio, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error === "not_found") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center" data-testid="verify-not-found">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Folio No Encontrado</h1>
          <p className="text-gray-500">El folio <span className="font-mono font-bold">{folio || token}</span> no existe en nuestro sistema.</p>
          <p className="text-sm text-gray-400 mt-4">Si crees que es un error, contacta al laboratorio.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Error de Verificacion</h1>
          <p className="text-gray-500">No se pudo verificar el folio. Intenta de nuevo mas tarde.</p>
        </div>
      </div>
    );
  }

  const statusMap = {
    completed: { label: "Completado", color: "text-green-600", bg: "bg-green-50", icon: CheckCircle },
    in_progress: { label: "En Proceso", color: "text-amber-600", bg: "bg-amber-50", icon: Clock },
    pending: { label: "Pendiente", color: "text-gray-500", bg: "bg-gray-50", icon: Clock },
  };
  const status = statusMap[data.status] || statusMap.pending;
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full overflow-hidden" data-testid="verify-success">
        {/* Header */}
        <div className="bg-white px-6 pt-4 pb-2 text-center">
          {data.logo_url ? (
            <img src={data.logo_url} alt="Logo" className="h-36 mx-auto object-contain" />
          ) : (
            <h1 className="text-2xl font-bold text-blue-900">ComaLab</h1>
          )}
        </div>

        {/* Verification Badge */}
        <div className="flex justify-center -mt-1">
          <div className="bg-green-500 rounded-full p-3 shadow-md">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
        </div>

        <div className="px-6 pt-4 pb-6">
          <h2 className="text-center text-green-700 font-bold text-base mb-1">Resultados Verificados</h2>
          <p className="text-center text-gray-400 text-xs mb-5">Este folio es autentico y fue procesado por ComaLab</p>

          {/* Patient Info */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="text-gray-500 w-20">Folio</span>
              <span className="font-mono font-bold text-gray-900">{data.folio}</span>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <User className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="text-gray-500 w-20">Paciente</span>
              <span className="font-semibold text-gray-900">{data.patient_name}</span>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="text-gray-500 w-20">Fecha</span>
              <span className="text-gray-700">{data.date} {data.time && `- ${data.time}`}</span>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <Stethoscope className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="text-gray-500 w-20">Medico</span>
              <span className="text-gray-700">{data.doctor}</span>
            </div>

            {data.branch && (
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <span className="text-gray-500 w-20">Sucursal</span>
                <span className="text-gray-700">{data.branch}</span>
              </div>
            )}

            {/* Status */}
            <div className="flex items-center gap-3 text-sm">
              <StatusIcon className={`w-4 h-4 ${status.color} flex-shrink-0`} />
              <span className="text-gray-500 w-20">Estado</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                {status.label}
              </span>
            </div>
          </div>

          {/* Studies List */}
          {data.studies && data.studies.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center gap-2 mb-2">
                <FlaskConical className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-semibold text-gray-700">Estudios Realizados ({data.total_studies})</span>
              </div>
              <div className="bg-gray-50 rounded-lg divide-y divide-gray-100">
                {data.studies.map((study, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs text-gray-700">{study.study_name}</span>
                    {study.validated ? (
                      <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                    ) : (
                      <Clock className="w-3.5 h-3.5 text-amber-400" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <p className="text-[10px] text-gray-400">Seguridad y Calidad en los Resultados</p>
            <p className="text-[10px] text-gray-300 mt-1">comalab.mx</p>
          </div>
        </div>
      </div>
    </div>
  );
}
